<template>
  <div class="text-share-page">
    <div class="card">
      <h2>文字分享</h2>
      
      <div v-if="shareUrl" class="share-success">
        <div class="success-icon">✓</div>
        <h3>分享创建成功！</h3>
        <div class="info-item">
          <label>您的文字已保存，可以通过以下链接分享：</label>
          <div class="share-url">
            <input type="text" readonly :value="shareUrl" ref="urlInput" />
            <button class="btn btn-copy" @click="copyShareUrl">
              <span class="btn-icon">📋</span>
              <span>复制</span>
            </button>
          </div>
        </div>
        
        <div class="share-settings-info">
          <div class="setting-info">
            <span class="setting-icon">🔄</span>
            <span>
              阅读次数: 
              <strong>{{ maxReads === '-1' ? '不限' : maxReads + '次' }}</strong>
              {{ maxReads === '1' ? '(阅后即焚)' : '' }}
            </span>
          </div>
          <div class="setting-info">
            <span class="setting-icon">⏱️</span>
            <span>
              有效期至: 
              <strong>{{ formatExpiryDate() }}</strong>
            </span>
          </div>
        </div>
        
        <div class="actions">
          <button class="btn btn-primary" @click="resetForm">创建新分享</button>
        </div>
      </div>
      
      <div v-else class="share-form">
        <div class="form-group">
          <label for="shareText">请输入您想分享的文字：</label>
          <textarea 
            id="shareText" 
            v-model="shareText" 
            rows="6" 
            placeholder="在这里输入您想分享的文字内容..."
          ></textarea>
          <div class="char-count" :class="{ 'warning': shareText.length > 5000 }">
            {{ shareText.length }}/10000
          </div>
        </div>
        
        <div class="form-group">
          <label for="authorName">您的名称（可选）：</label>
          <input 
            type="text" 
            id="authorName" 
            v-model="authorName" 
            placeholder="请输入您的名称"
          />
        </div>
        
        <div class="form-group">
          <label>分享设置：</label>
          <div class="settings-row">
            <div class="setting-item">
              <label for="maxReads">
                <span class="setting-icon">🔄</span>
                最大阅读次数：
              </label>
              <select id="maxReads" v-model="maxReads">
                <option value="1">1次（阅后即焚）</option>
                <option value="3">3次</option>
                <option value="5">5次</option>
                <option value="10">10次</option>
                <option value="-1">不限制</option>
              </select>
            </div>
            
            <div class="setting-item">
              <label for="expiryHours">
                <span class="setting-icon">⏱️</span>
                有效期：
              </label>
              <select id="expiryHours" v-model="expiryHours">
                <option value="1">1小时</option>
                <option value="6">6小时</option>
                <option value="12">12小时</option>
                <option value="24">24小时</option>
                <option value="72">3天</option>
                <option value="168">7天</option>
              </select>
            </div>
          </div>
        </div>
        
        <div class="actions">
          <button 
            class="btn btn-primary" 
            @click="generateShareUrl" 
            :disabled="!shareText.trim() || isGenerating"
          >
            <span v-if="isGenerating" class="loading-dot"></span>
            {{ isGenerating ? '生成中...' : '生成分享链接' }}
          </button>
        </div>
      </div>
    </div>
    
    <div v-if="copySuccess" class="copy-success">
      复制成功！
    </div>
  </div>
</template>

<script>
import fileService from '../utils/fileService.js'

export default {
  name: 'TextSharePage',
  data() {
    return {
      shareText: '',
      authorName: '',
      shareUrl: '',
      maxReads: 1, // 默认阅后即焚
      expiryHours: 24, // 默认24小时过期
      isGenerating: false,
      copySuccess: false,
      isMobile: false,
      expiryDate: null
    }
  },
  mounted() {
    this.checkMobile()
    // 监听窗口大小变化，以便响应式调整
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    // 移除事件监听器
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    // 检查是否为移动设备
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    
    async generateShareUrl() {
      try {
        if (!this.shareText.trim()) {
          alert('请输入要分享的文字内容')
          return
        }
        
        if (this.shareText.length > 10000) {
          alert('文字内容过长，请控制在10000字以内')
          return
        }
        
        this.isGenerating = true
        
        // 创建要分享的数据对象
        const shareData = {
          text: this.shareText,
          authorName: this.authorName || '匿名用户',
          timestamp: new Date().toISOString()
        }
        
        // 生成唯一文件ID
        const fileId = fileService.generateFileId()
        
        // 计算过期时间
        const expiresAt = new Date()
        expiresAt.setHours(expiresAt.getHours() + parseInt(this.expiryHours))
        this.expiryDate = expiresAt
        
        try {
          // 保存到文件服务
          await fileService.saveFile(fileId, shareData, {
            maxReads: parseInt(this.maxReads),
            expiresAt: expiresAt.toISOString()
          })
          
          // 生成分享URL
          const baseUrl = window.location.origin
          this.shareUrl = `${baseUrl}/#/text/${fileId}`
          
          // 延迟一下，让用户感知到生成过程
          setTimeout(() => {
            this.isGenerating = false
          }, 500)
        } catch (saveError) {
          console.error('保存文件失败:', saveError)
          throw new Error(saveError.message || '保存分享内容失败')
        }
      } catch (error) {
        console.error('生成分享链接失败:', error)
        alert('生成分享链接失败: ' + (error.message || '未知错误'))
        this.isGenerating = false
      }
    },
    
    copyShareUrl() {
      const urlInput = this.$refs.urlInput
      urlInput.select()
      try {
        // 尝试使用现代Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(this.shareUrl)
            .then(() => {
              this.showCopySuccess()
            })
            .catch(() => {
              // 如果Clipboard API失败，回退到execCommand
              document.execCommand('copy')
              this.showCopySuccess()
            })
        } else {
          // 回退到传统方法
          document.execCommand('copy')
          this.showCopySuccess()
        }
      } catch (err) {
        console.error('复制失败:', err)
        alert('复制失败，请手动复制链接')
      }
    },
    
    showCopySuccess() {
      this.copySuccess = true
      setTimeout(() => {
        this.copySuccess = false
      }, 2000)
    },
    
    resetForm() {
      this.shareText = ''
      this.authorName = ''
      this.shareUrl = ''
      this.expiryDate = null
    },
    
    formatExpiryDate() {
      if (!this.expiryDate) {
        const expiresAt = new Date()
        expiresAt.setHours(expiresAt.getHours() + parseInt(this.expiryHours))
        return this.formatDate(expiresAt)
      }
      return this.formatDate(this.expiryDate)
    },
    
    formatDate(date) {
      try {
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (e) {
        return '未知时间'
      }
    }
  }
}
</script>

<style scoped>
.text-share-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 2px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

.card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 10px;
  transition: all 0.3s ease;
}

h2 {
  text-align: center;
  margin-bottom: 30px;
  color: #07C160;
  font-size: 24px;
  font-weight: 600;
}

h3 {
  text-align: center;
  margin-bottom: 20px;
  color: #333;
  font-size: 18px;
}

.form-group {
  margin-bottom: 25px;
  position: relative;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
  font-size: 16px;
}

textarea, input[type="text"] {
  width: 100%;
  padding: 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s ease;
}

textarea:focus, input[type="text"]:focus {
  outline: none;
  border-color: #07C160;
  box-shadow: 0 0 0 2px rgba(7, 193, 96, 0.2);
}

.char-count {
  position: absolute;
  bottom: -20px;
  right: 0;
  font-size: 12px;
  color: #888;
}

.char-count.warning {
  color: #ff9800;
}

/* 成功页面样式 */
.share-success {
  padding: 25px;
  background-color: #f9f9f9;
  border-radius: 12px;
  text-align: center;
}

.success-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #07C160;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  margin: 0 auto 20px;
}

.info-item {
  margin-bottom: 25px;
}

.info-item label {
  display: block;
  font-weight: 500;
  margin-bottom: 15px;
  color: #555;
}

.share-url {
  display: flex;
  align-items: center;
  margin-top: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.share-url input {
  flex: 1;
  padding: 14px;
  border: 1px solid #e0e0e0;
  border-right: none;
  border-radius: 8px 0 0 8px;
  font-size: 14px;
  background-color: #fff;
}

.share-settings-info {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin: 20px 0;
  padding-top: 15px;
  padding-left: 5px;
  padding-right: 5px;
  padding-bottom: 15px;
  background-color: #f0f9f4;
  border-radius: 8px;
}

.setting-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #555;
}

.setting-icon {
  font-size: 18px;
  margin-right: 5px;
}

/* 按钮样式 */
.actions {
  margin-top: 30px;
  display: flex;
  justify-content: center;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}

.btn-icon {
  margin-right: 8px;
  font-size: 18px;
}

.btn-primary {
  background-color: #07C160;
  color: white;
  box-shadow: 0 2px 8px rgba(7, 193, 96, 0.3);
  min-width: 160px;
}

.btn-primary:hover {
  background-color: #06a050;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(7, 193, 96, 0.4);
}

.btn-primary:disabled {
  background-color: #a0a0a0;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-copy {
  background-color: #07C160;
  color: white;
  border-radius: 0 8px 8px 0;
  padding: 14px 20px;
  white-space: nowrap;
}

.btn-copy:hover {
  background-color: #06a050;
}

/* 设置项样式 */
.settings-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 15px;
}

.setting-item {
  flex: 1;
  min-width: 200px;
}

.setting-item label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #555;
  font-size: 14px;
}

.setting-item select {
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  font-size: 15px;
  transition: all 0.3s ease;
}

.setting-item select:focus {
  outline: none;
  border-color: #07C160;
  box-shadow: 0 0 0 2px rgba(7, 193, 96, 0.2);
}

/* 加载动画 */
.loading-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: white;
  margin-right: 10px;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { transform: scale(0.8); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(0.8); opacity: 0.5; }
}

/* 复制成功提示 */
.copy-success {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(7, 193, 96, 0.9);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  animation: fadeInOut 2s ease;
  z-index: 1000;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translate(-50%, 20px); }
  20% { opacity: 1; transform: translate(-50%, 0); }
  80% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, -20px); }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .text-share-page {
    padding: 15px;
  }
  
  .card {
    padding: 20px;
    border-radius: 10px;
  }
  
  h2 {
    font-size: 20px;
    margin-bottom: 20px;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  textarea, input[type="text"] {
    padding: 12px;
    font-size: 15px;
  }
  
  .settings-row {
    flex-direction: column;
    gap: 15px;
  }
  
  .setting-item {
    width: 100%;
  }
  
  .share-url {
    flex-direction: column;
    border-radius: 8px;
    overflow: visible;
    box-shadow: none;
  }
  
  .share-url input {
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    margin-bottom: 10px;
  }
  
  .btn-copy {
    width: 100%;
    border-radius: 8px;
  }
  
  .share-settings-info {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
}
</style>